// web-commands.ts — the PER-APP half of the web shape: maps oriora_lockbox's Tauri commands
// (normally served by ipc.rs/db.rs/signals.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. Local FTS retrieval + the on-device key-date/headline-fact
// derivation are NATIVE work; on web they return valid-but-EMPTY shells (degraded until ported).

import type { CommandHandler, WebStore } from "./web-runtime";
import { deriveKeyDates, deriveHeadlineFacts, searchPassages, normalizeKind } from "./lockbox-web";
import { browserSearch, browserFetch } from "./web-search-browser";

const DOCUMENTS = "documents";
const KEY_DATES = "key_dates";

function uuid(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function stripBody(r: Record<string, unknown>): Record<string, unknown> {
  const { body: _body, ...meta } = r;
  return meta;
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Documents (CRUD) ──
    list_documents: () => db.all(DOCUMENTS).map((r) => stripBody(r)),
    get_document: ({ id }) => db.get(DOCUMENTS, String(id)),
    document_count: () => db.all(DOCUMENTS).length,
    // Mirrors native add_document: normalise title/kind, derive key dates ON-DEVICE
    // (lockbox-web.ts = the ledger.rs port), store document + its ledger rows together.
    add_document: ({ input }) => {
      const doc = (input ?? {}) as { title?: string; kind?: string; body?: string };
      const body = String(doc.body ?? "");
      const title = String(doc.title ?? "").trim() || "Untitled document";
      const keyDates = deriveKeyDates(body);
      const row = {
        id: uuid(),
        title,
        kind: normalizeKind(String(doc.kind ?? "other")),
        body,
        char_count: [...body].length,
        key_date_count: keyDates.length,
        created_at: nowIso(),
        updated_at: nowIso(),
        photo: "",
      };
      db.insert(DOCUMENTS, row);
      for (const [label, date, context] of keyDates) {
        db.insert(KEY_DATES, { id: uuid(), doc_id: row.id, label, date, context, created_at: nowIso() });
      }
      return stripBody(row);
    },
    delete_document: ({ id }) => {
      // Native FK-cascade removes the doc's key dates; mirror that here.
      for (const k of db.all(KEY_DATES).filter((r) => String(r.doc_id) === String(id))) {
        db.remove(KEY_DATES, String(k.id));
      }
      db.remove(DOCUMENTS, String(id));
      return null;
    },
    // Attach a photo to a document — filed ON-DEVICE, never sent to the model (web mirror of the native command).
    set_document_photo: ({ id, photo }) => {
      db.update(DOCUMENTS, String(id), { photo: String(photo ?? ""), updated_at: nowIso() });
      return null;
    },

    // ── Key dates ledger + retrieval + derivation — 1:1 ports (lockbox-web.ts). ──
    // NB: web invoke shim passes args VERBATIM — store.ts sends camelCase docIds.
    all_key_dates: ({ limit }) => {
      const cap = Math.min(Math.max(Number(limit ?? 200) || 200, 1), 1000);
      const byDoc = new Map(db.all(DOCUMENTS).map((d) => [String(d.id), d]));
      return db.all(KEY_DATES)
        .map((k) => joinKeyDate(k, byDoc))
        .filter((k) => k != null)
        .sort((a, b) => String(a!.date).localeCompare(String(b!.date)))
        .slice(0, cap);
    },
    upcoming_key_dates: ({ today, limit }) => {
      const t = /^\d{4}-\d{2}-\d{2}/.test(String(today ?? "")) ? String(today).slice(0, 10) : todayLocal();
      const cap = Math.min(Math.max(Number(limit ?? 50) || 50, 1), 500);
      const byDoc = new Map(db.all(DOCUMENTS).map((d) => [String(d.id), d]));
      return db.all(KEY_DATES)
        .filter((k) => String(k.date) >= t)
        .map((k) => joinKeyDate(k, byDoc))
        .filter((k) => k != null)
        .sort((a, b) => String(a!.date).localeCompare(String(b!.date)))
        .slice(0, cap);
    },
    search_doc_passages: ({ query, limit }) => {
      const cap = Math.min(Math.max(Number(limit ?? 12) || 12, 1), 24);
      const docs = db.all(DOCUMENTS).map((d) => ({
        id: String(d.id), title: String(d.title ?? ""), kind: String(d.kind ?? "other"), body: String(d.body ?? ""),
      }));
      return searchPassages(docs, String(query ?? ""), cap);
    },
    headline_facts_for_docs: ({ docIds, today, limit }) => {
      const t = /^\d{4}-\d{2}-\d{2}/.test(String(today ?? "")) ? String(today).slice(0, 10) : todayLocal();
      const cap = Math.min(Math.max(Number(limit ?? 6) || 6, 1), 12);
      const ids: string[] = [];
      const seenIds = new Set<string>();
      for (const raw of Array.isArray(docIds) ? docIds : []) {
        const id = String(raw);
        if (!seenIds.has(id)) { seenIds.add(id); ids.push(id); }
      }
      const all: { text: string; tone: string; rank: number }[] = [];
      for (const id of ids) {
        const doc = db.all(DOCUMENTS).find((d) => String(d.id) === id);
        if (!doc) continue;
        const dates = db.all(KEY_DATES)
          .filter((k) => String(k.doc_id) === id)
          .sort((a, b) => String(a.date).localeCompare(String(b.date)))
          .map((k) => [String(k.label), String(k.date), String(k.context ?? "")] as [string, string, string]);
        all.push(...deriveHeadlineFacts(String(doc.body ?? ""), String(doc.kind ?? "other"), dates, t));
      }
      all.sort((a, b) => b.rank - a.rank);
      const out: typeof all = [];
      const fseen = new Set<string>();
      for (const f of all) {
        if (out.length >= cap) break;
        if (!fseen.has(f.text)) { fseen.add(f.text); out.push(f); }
      }
      return out;
    },
  };
}

/** Native queries JOIN documents for doc_title + kind; mirror that shape. */
function joinKeyDate(k: Record<string, unknown>, byDoc: Map<string, Record<string, unknown>>) {
  const doc = byDoc.get(String(k.doc_id));
  if (!doc) return null;
  return {
    id: String(k.id), doc_id: String(k.doc_id),
    doc_title: String(doc.title ?? ""), kind: String(doc.kind ?? "other"),
    label: String(k.label ?? ""), date: String(k.date ?? ""), context: String(k.context ?? ""),
  };
}
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
